import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Phone, Mail, MapPin, IndianRupee, Clock, User as UserIcon, FileText, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const SCORE_STYLE = { HOT: "bg-rose-600", HIGH: "bg-orange-500", MEDIUM: "bg-amber-500", LOW: "bg-slate-400" };
const STAGES = ["new","contacted","interested","qualified","follow_up","site_visit","negotiation","won","lost"];

export default function LeadDetail() {
  const { lid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [associates, setAssociates] = useState([]);
  const [assignTo, setAssignTo] = useState("");

  const load = async () => {
    const r = await api.get(`/leads/${lid}`);
    setData(r.data); setNotes(r.data.lead?.notes || "");
    setAssignTo(r.data.lead?.assigned_to || "");
  };
  useEffect(() => { load(); if (user?.role === "company_admin") api.get("/users").then(r => setAssociates(r.data.filter(u => u.role === "sales_associate" && u.is_active))); }, [lid]);

  if (!data) return <div className="p-8 text-slate-500">Loading…</div>;
  const L = data.lead; const contact = data.contact || {};

  const changeStatus = async (v) => {
    await api.put(`/leads/${lid}`, { status: v });
    toast.success(`Moved to ${v}`); load();
  };
  const saveNotes = async () => {
    await api.put(`/leads/${lid}`, { notes });
    toast.success("Notes saved");
  };
  const doAssign = async () => {
    if (!assignTo || assignTo === L.assigned_to) return;
    const target = assignTo === "__admin__" ? null : assignTo;
    try {
      await api.post(`/leads/${lid}/assign`, { assigned_to: target });
      toast.success("Reassigned"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto space-y-6" data-testid="lead-detail-page">
      <button onClick={()=>nav(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="w-4 h-4"/>Back</button>

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold text-slate-900">{L.name || "(no name)"}</h1>
            <Badge className={`${SCORE_STYLE[L.score]} text-white`}>{L.score}</Badge>
            <Badge variant="outline">{L.status.replace("_"," ")}</Badge>
          </div>
          <div className="mt-2 flex items-center gap-4 flex-wrap text-sm text-slate-600">
            <span className="flex items-center gap-1"><Phone className="w-3 h-3"/>{L.phone}</span>
            {contact.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3"/>{contact.email}</span>}
            {L.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/>{L.location}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Select value={L.status} onValueChange={changeStatus}>
            <SelectTrigger className="w-44" data-testid="lead-stage-select"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-white">
              {STAGES.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}
            </SelectContent>
          </Select>
          {data.conversation && <Button variant="outline" onClick={()=>nav(`${user?.role==="sales_associate"?"/sales":""}/inbox/${data.conversation.id}`)} data-testid="open-conversation-button">
            <MessageSquare className="w-4 h-4 mr-1"/>Open chat</Button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 border-slate-200 lg:col-span-2 space-y-4">
          <div>
            <h3 className="font-semibold text-slate-900 mb-2">AI Qualification</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <Field icon={FileText} label="Requirement" value={L.requirement}/>
              <Field icon={IndianRupee} label="Budget" value={L.budget}/>
              <Field icon={MapPin} label="Location" value={L.location}/>
              <Field icon={Clock} label="Timeline" value={L.timeline}/>
            </div>
            {L.ai_summary && (
              <div className="mt-3 p-3 bg-indigo-50 border border-indigo-100 rounded-lg text-sm text-indigo-900" data-testid="lead-ai-summary">
                <div className="text-xs font-semibold text-indigo-700 mb-1">Aria's summary</div>
                {L.ai_summary}
              </div>
            )}
          </div>

          <div>
            <h3 className="font-semibold text-slate-900 mb-2">Notes</h3>
            <Textarea rows={4} value={notes} onChange={(e)=>setNotes(e.target.value)} placeholder="Add your notes…" data-testid="lead-notes-input"/>
            <div className="mt-2 flex justify-end"><Button size="sm" onClick={saveNotes} className="bg-emerald-600 hover:bg-emerald-700" data-testid="lead-save-notes-button">Save notes</Button></div>
          </div>

          {data.messages?.length > 0 && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Conversation ({data.messages.length})</h3>
              <div className="max-h-60 overflow-y-auto space-y-1 text-xs bg-slate-50 rounded-lg p-3">
                {data.messages.slice(-15).map(m => (
                  <div key={m.id} className={`${m.direction === "inbound" ? "text-slate-700" : "text-emerald-700"}`}>
                    <b>{m.direction === "inbound" ? "Customer" : (m.sender === "ai" ? "Aria" : "You")}:</b> {m.body}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <div className="space-y-4">
          <Card className="p-5 border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-2">Assignment</h3>
            <div className="text-sm text-slate-600 mb-2 flex items-center gap-2">
              <UserIcon className="w-4 h-4"/>
              {data.assigned_user ? `${data.assigned_user.name}` : "Company Admin"}
            </div>
            <div className="text-xs text-slate-500">Method: {L.assignment_method || "—"}</div>
            {user?.role === "company_admin" && (
              <div className="mt-3 space-y-2">
                <Select value={assignTo} onValueChange={setAssignTo}>
                  <SelectTrigger data-testid="assign-select"><SelectValue placeholder="Choose associate"/></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="__admin__" data-testid="assign-option-admin">Company Admin (unassign)</SelectItem>
                    {associates.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.lead_count || 0})</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={doAssign} data-testid="assign-button">Reassign</Button>
              </div>
            )}
          </Card>

          <Card className="p-5 border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-2">Assignment history</h3>
            <div className="space-y-2 max-h-40 overflow-y-auto text-xs">
              {(L.assignment_history || []).length === 0 ? <div className="text-slate-400">No history</div> :
                L.assignment_history.map((h, i) => (
                  <div key={i} className="p-2 bg-slate-50 rounded">
                    <div className="font-medium text-slate-900">{h.method}</div>
                    <div className="text-slate-500">{new Date(h.assigned_at).toLocaleString()}</div>
                  </div>
                ))}
            </div>
          </Card>

          <Card className="p-5 border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-2">Follow-ups ({data.follow_ups?.length || 0})</h3>
            <div className="space-y-1 text-xs">
              {(data.follow_ups || []).slice(0,5).map(fu => (
                <div key={fu.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <span className="capitalize">{fu.kind}</span>
                  <span className="text-slate-500">{new Date(fu.due_at).toLocaleDateString()}</span>
                </div>
              ))}
              {(data.follow_ups || []).length === 0 && <div className="text-slate-400">None scheduled</div>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="p-3 bg-slate-50 rounded-lg">
      <div className="flex items-center gap-1 text-[11px] text-slate-500 uppercase font-medium mb-1"><Icon className="w-3 h-3"/>{label}</div>
      <div className="text-sm font-medium text-slate-900">{value || "—"}</div>
    </div>
  );
}
